import { Alert, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useState } from 'react';
import { useFormItem } from '../../common/hooks/use-form-item/index';
import { InputType } from '../../common/hooks/use-form-item/types';
import { required } from '../../common/hooks/use-form-item/validators/existential';
import { maxLength } from '../../common/hooks/use-form-item/validators/operational';
import { UserLevel, compareUserLevel, getUserLevelLabel } from '../../organization/users/enums/UserLevel';
import { UserRole } from '../../organization/users/enums/UserRole';
import { ButtonBox, PrimaryButton, TertiaryButton } from './Buttons';
import { RolesAutocomplete } from './RolesAutocomplete';

type Props = {
  useRoles: boolean;
  prefill: {
    isCurrentUser: boolean;
    preferredName: string;
    preferredNameDisabled: boolean;
    level: UserLevel;
    roles: UserRole[];
    levelDisabled: boolean;
  };
  submit: {
    label: string;
    action: (preferredName: string | null, level: UserLevel | null, roles: UserRole[] | null) => Promise<void>;
  };
  back: {
    label: string;
    action: () => void;
  };
};

export const UpdateUserForm = ({ useRoles, prefill, submit, back }: Props) => {
  const { isCurrentUser, preferredName: prefillName, preferredNameDisabled, level: prefillLevel, roles: prefillRoles, levelDisabled } = prefill;

  const permissionDisabledReason = levelDisabled
    ? isCurrentUser
      ? t(`updateUser.disclaimers.${useRoles ? 'selfRolesUpdateDisabled' : 'selfPermissionUpdateDisabled'}`)
      : t(`updateUser.disclaimers.${useRoles ? 'rolesUpdateDisabled' : 'permissionUpdateDisabled'}`)
    : undefined;

  const [loading, setLoading] = useState<boolean>(false);
  const [preferredName, preferredNameComponent, preferredNameValid] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t('updateUserForm.preferredName.input'),
    value: prefillName,
    validator: maxLength(1000),
    inputOptions: {
      autoFocus: true,
      disabled: loading || preferredNameDisabled,
      focusedLabel: t('updateUserForm.preferredName.focused'),
      help: preferredNameDisabled
        ? isCurrentUser
          ? t('updateUser.disclaimers.selfPreferredNameUpdateDisabled')
          : t('updateUser.disclaimers.preferredNameUpdateDisabled')
        : undefined,
    },
    textOptions: { trimOnBlur: true },
  });

  const [level, levelComponent, levelValid] = useFormItem<UserLevel>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t('updateUserForm.level'),
    value: prefillLevel,
    validator: useRoles ? null : required(),
    inputOptions: {
      disabled: loading || levelDisabled,
      help: permissionDisabledReason,
      showRequiredIndicator: true,
    },
    autocompleteOptions: {
      readOnly: true,
      clearable: false,
      options: [UserLevel.TESTER, UserLevel.ANALYST, UserLevel.ADMIN].map((l) => ({ value: l, label: getUserLevelLabel(l) })),
    },
  });

  const [roles, setRoles] = useState<UserRole[]>(prefillRoles);

  const valuesValid = (!useRoles && !!level) || (useRoles && roles.length > 0);
  const formValid = !(preferredNameDisabled && levelDisabled) && preferredNameValid && levelValid;
  const canSubmit = !loading && valuesValid && formValid;

  const comparePermission = compareUserLevel(prefillLevel, level);
  const levelLabel = getUserLevelLabel(level);
  const rolesChanged = prefillRoles.length !== roles.length || prefillRoles.some((r) => !roles.includes(r));

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setLoading(true);
      const newPreferredName = prefillName === preferredName ? null : preferredName;
      const newLevel = comparePermission !== 0 ? level : null;
      const newRoles = rolesChanged ? roles : null;
      if (useRoles) {
        await submit.action(newPreferredName, null, newRoles);
      } else {
        await submit.action(newPreferredName, newLevel, null);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={1.5} component="form" onSubmit={onSubmit}>
      {!useRoles && comparePermission !== 0 && (
        <Alert severity="warning">
          {comparePermission === -1
            ? t('updateUser.disclaimers.permissionDowngrade', { level: levelLabel })
            : t('updateUser.disclaimers.permissionUpgrade', { level: levelLabel })}
        </Alert>
      )}
      {useRoles && rolesChanged && <Alert severity="warning">{t('updateUser.disclaimers.rolesChanged')}</Alert>}
      {preferredNameComponent}
      {!useRoles && levelComponent}
      {useRoles && (
        <RolesAutocomplete
          label={t('updateUserForm.roles.input')}
          focusedLabel={t('updateUserForm.roles.focused')}
          disabledReason={loading ? '' : permissionDisabledReason ?? null}
          values={roles}
          onChange={setRoles}
        />
      )}
      <ButtonBox>
        <TertiaryButton disabled={loading} onClick={back.action}>
          {back.label}
        </TertiaryButton>
        <PrimaryButton disabled={!canSubmit} type="submit">
          {submit.label}
        </PrimaryButton>
      </ButtonBox>
    </Stack>
  );
};
